import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Notification } from '@offerzen/pinata'
import { useForm } from 'react-hook-form'
import { cx } from '@emotion/css'
import computer from '../../../assets/computer-with-checklist.svg'
import Card from '../../components/Card'
import CardFooterNav from '../../components/CardFooterNav/CardFooterNav'
import styles from './styles'
import CityMultiSelect from '../../../CandidateExperience/components/CityMultiSelect'
import ControlledHookField from '../../../CandidateExperience/components/ControlledHookField'

import { previousOnboardingUrl } from '../../helpers/onboardingSteps'
import { formatPreferredLocations } from '../../../CandidateExperience/helpers/candidateHelpers'

const validators = {
  requiredArray: value => (value?.length ? undefined : 'Required'),
}

const WorkplacePolicyForm = ({ availableCountries, defaultValues, navProps, onSubmit, locationOsmStrings }) => {
  const hybridPreferredLocations = useRef([])
  const officePreferredLocations = useRef([])

  const { control, watch, formState, handleSubmit, setError, clearErrors } = useForm({
    mode: 'onBlur',
    defaultValues,
  })

  const checkedPolicies = watch('policies', {})

  const isAnyPolicyChecked = checkedPolicies.remote || checkedPolicies.hybrid || checkedPolicies.office

  const { isSubmitting, isValid: isFormValid, errors } = formState

  const isSubmitDisabled = Object.keys(errors).length > 0
  const isSubmittable = isAnyPolicyChecked && isFormValid

  const submitForm = e => {
    if (isSubmittable) {
      onSubmit({
        ...e,
        comprehensiveLocations: {
          office: officePreferredLocations.current,
          hybrid: hybridPreferredLocations.current,
        },
      })
    }
    if (!isAnyPolicyChecked) setError('policies', { type: 'error-policies', message: 'Select at least one remote preference.' })
  }

  const selectedHybridCities = watch('cities.hybrid')
  const selectedOfficeCities = watch('cities.office')

  useEffect(() => {
    const { selectedPreferredLocations: selectedHybridPreferredLocations } = formatPreferredLocations({
      cities: selectedHybridCities,
      locationOsmStrings,
    })

    hybridPreferredLocations.current = selectedHybridPreferredLocations
  }, [selectedHybridCities, locationOsmStrings])

  useEffect(() => {
    const { selectedPreferredLocations: selectedOfficePreferredLocations } = formatPreferredLocations({
      cities: selectedOfficeCities,
      locationOsmStrings,
    })

    officePreferredLocations.current = selectedOfficePreferredLocations
  }, [selectedOfficeCities, locationOsmStrings])

  return (
    <Card title="Location and remote preferences" imageUrl={computer} width={550}>
      <form onSubmit={handleSubmit(submitForm)}>
        <h2 className={cx(styles.formHeading, styles.paddingLarge)}>How would you like to work?</h2>

        <ControlledHookField
          className={styles.checkbox}
          id="policies-remote"
          name="policies.remote"
          value="remote"
          label="100% remote"
          component={Checkbox}
          control={control}
          onChange={() => {
            clearErrors('policies')
          }}
        />

        <ControlledHookField
          className={styles.checkbox}
          id="polcies-hybrid"
          name="policies.hybrid"
          value="hybrid"
          label="Hybrid"
          component={Checkbox}
          control={control}
          onChange={() => {
            clearErrors(['policies', 'cities.hybrid'])
          }}
        />
        <If condition={checkedPolicies.hybrid}>
          <ControlledHookField
            className={styles.select}
            id="cities-hybrid"
            name="cities.hybrid"
            label="Where should the company be located for hybrid work?"
            component={CityMultiSelect}
            countries={availableCountries}
            validate={[validators.requiredArray]}
            control={control}
            onChange={() => {
              clearErrors('cities.hybrid')
            }}
          />
        </If>

        <ControlledHookField
          className={styles.checkbox}
          id="policies-office"
          name="policies.office"
          value="office"
          label="Office-based"
          component={Checkbox}
          control={control}
          onChange={() => {
            clearErrors(['policies', 'cities.office'])
          }}
        />
        <If condition={checkedPolicies.office}>
          <ControlledHookField
            className={styles.select}
            id="cities-office"
            name="cities.office"
            component={CityMultiSelect}
            label="Where should the company be located for office-based work?"
            countries={availableCountries}
            validate={[validators.requiredArray]}
            control={control}
            onChange={() => {
              clearErrors('cities.office')
            }}
          />
        </If>
        <If condition={!!errors.policies}>
          <Notification theme="error" className={styles.notification}>
            Select at least one remote preference.
          </Notification>
        </If>

        <CardFooterNav
          className={styles.footer}
          submitDisabled={isSubmitDisabled}
          loading={isSubmitting}
          navigateBack={() => navProps.history.push(previousOnboardingUrl(navProps.location.pathname))}
        />
      </form>
    </Card>
  )
}

export default WorkplacePolicyForm

const { string, bool, arrayOf, shape, func } = PropTypes
WorkplacePolicyForm.propTypes = {
  defaultValues: shape({
    policies: shape({ remote: bool, hybrid: bool, office: bool }),
    cities: shape({ hybrid: arrayOf(string), office: arrayOf(string) }),
  }),
  navProps: shape({ history: arrayOf(shape({})), location: shape({ pathname: string }) }),
  availableCountries: arrayOf(
    shape({
      id: string,
      name: string,
      code: string,
      cities: arrayOf(
        shape({
          id: string,
          name: string,
        }),
      ),
    }).isRequired,
  ),
  onSubmit: func,
  locationOsmStrings: shape({}),
}
